import { useMemo, useEffect } from 'react';
import { useWorkOrdersStore } from '@stores/workOrdersStore';
import { useWorkOrdersOData } from '@api/enterprise/workorder/queries/useWorkOrdersOData';
import { useUserStore } from '@stores/userStore';
import { isOverdue } from '@pages/Work/WorkOrderListPage/utils/dateUtils';
import useOnlineStatus from '@hooks/useOnlineStatus';
import type { OrderStatus } from '@api/enterprise/openapi/models/order-status';

export type StatusFilter =
  | 'all'
  | 'Incomplete'
  | 'Complete'
  | 'Invoiced'
  | 'Cancelled';

type SidebarWorkOrder = {
  jobNumber: string;
  customerName: string;
  dueDate: string | null;
  status: OrderStatus | undefined;
  isOverdue: boolean;
  isDueToday: boolean;
};

type UseSidebarWorkOrdersOptions = {
  statusFilter?: StatusFilter;
};

export const useSidebarWorkOrders = (
  options: UseSidebarWorkOrdersOptions = {}
) => {
  const { statusFilter = 'Incomplete' } = options;

  const {
    workOrders: storeWorkOrders,
    isLoading: storeLoading,
    error: storeError,
    setWorkOrders,
    setLoading,
    setError
  } = useWorkOrdersStore();

  const { employeeId, employeeCompany, isAuthenticated } = useUserStore();
  const isOnline = useOnlineStatus();

  const shouldFetch =
    isAuthenticated &&
    isOnline &&
    (!storeWorkOrders || storeWorkOrders.length === 0) &&
    !!employeeId &&
    !!employeeCompany;

  const {
    data,
    isLoading: queryLoading,
    error: queryError
  } = useWorkOrdersOData({
    queryParams: {
      $orderby: 'ExpectedDueDate asc',
      $top: 50,
      $count: true
    },
    associatedEmployeeUserName: employeeId,
    associatedEmployeeCompanyName: employeeCompany,
    enabled: shouldFetch
  });

  useEffect(() => {
    if (shouldFetch) {
      setLoading(queryLoading);
    }
  }, [queryLoading, shouldFetch, setLoading]);

  useEffect(() => {
    if (shouldFetch && queryError) {
      setError(queryError);
    }
  }, [queryError, shouldFetch, setError]);

  useEffect(() => {
    if (shouldFetch && data) {
      const rows = Array.isArray(data) ? data : data?.value || [];
      const count = Array.isArray(data)
        ? data.length
        : data['@odata.count'] || rows.length;
      if (rows.length > 0) {
        setWorkOrders(rows, count);
      }
    }
  }, [data, shouldFetch, setWorkOrders]);

  const workOrders = useMemo<SidebarWorkOrder[]>(() => {
    if (!storeWorkOrders || !Array.isArray(storeWorkOrders)) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return storeWorkOrders
      .filter(wo => {
        if (statusFilter === 'all') return true;
        return wo.status === statusFilter;
      })
      .map(wo => {
        const dueDate = wo.expectedDueDate
          ? new Date(wo.expectedDueDate)
          : null;
        const isDueToday =
          dueDate !== null && dueDate.toDateString() === today.toDateString();

        return {
          jobNumber: wo.jobNumber || '',
          customerName: wo.customerName || 'Unknown Customer',
          dueDate: wo.expectedDueDate || null,
          status: wo.status,
          isOverdue: isOverdue(wo.expectedDueDate),
          isDueToday
        };
      });
  }, [storeWorkOrders, statusFilter]);

  return {
    workOrders,
    isLoading: storeLoading || (shouldFetch && queryLoading),
    error: storeError || (shouldFetch ? queryError : null)
  };
};
